/**
 * Equipa — contas, papéis e zonas.
 *
 * Existe porque as pessoas mudam de função: um Field Agent do Zango passa a
 * Operations Manager, entra alguém novo no Kilamba, alguém sai. Isso é
 * operação normal e não pode depender de quem sabe mexer na base de dados.
 *
 * A palavra-passe é gerada pelo sistema e mostrada UMA vez. Não se pede ao
 * admin que invente uma — inventa sempre a mesma — nem se guarda para ser
 * consultada depois. Se se perder, gera-se outra.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  UserPlus, Loader2, MapPin, KeyRound, Ban, CheckCircle2, Copy, X, Shield,
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';
import { AuthContext } from '../context/AuthContext';

const CORES = {
  super_admin: 'bg-purple-500/15 text-purple-400',
  admin: 'bg-blue-500/15 text-blue-400',
  operations_manager: 'bg-cyan-500/15 text-cyan-400',
  supervisor: 'bg-amber-500/15 text-amber-400',
  field_agent: 'bg-green-500/15 text-green-400',
};

const StaffPage = () => {
  const { user } = useContext(AuthContext);
  const { success, error } = useToast();
  const [dados, setDados] = useState({ equipa: [], papeis: {} });
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novo, setNovo] = useState(null);        // formulário de criação
  const [senha, setSenha] = useState(null);      // credenciais acabadas de gerar
  const [busy, setBusy] = useState(false);
  const [aRetirar, setARetirar] = useState(null);  // { m, motivo }

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: e }, { data: z }] = await Promise.all([
        api.get('/admin/staff'),
        api.get('/operations/zones').catch(() => ({ data: [] })),
      ]);
      setDados(e || { equipa: [], papeis: {} });
      setZonas(z || []);
    } catch {
      error('Erro ao carregar a equipa');
    } finally { setLoading(false); }
  }, [error]);

  useEffect(() => { carregar(); }, [carregar]);

  const papeis = Object.entries(dados.papeis || {});
  const papeisAtribuiveis = papeis.filter(([, v]) =>
    user?.role === 'super_admin' || (v.criadoPor || []).includes(user?.role));
  const podeGerir = (m) => String(m.admin_id) !== String(user?.id)
    && (user?.role === 'super_admin'
      || (dados.papeis?.[m.role]?.criadoPor || []).includes(user?.role));
  // Atribuir alguém a uma zona desligada não serve de nada: o servidor recusa
  // o cadastro lá. Por isso só se oferecem as que estão a operar.
  const zonasActivas = zonas.filter((z) => z.activa);
  const zonasAtribuiveis = zonas;
  const precisaZonas = (papel) => dados.papeis?.[papel]?.zonas;

  const criar = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/admin/staff', {
        full_name: novo.full_name, email: novo.email, role: novo.role,
        zonas: precisaZonas(novo.role) ? novo.zonas : [],
      });
      setNovo(null);
      setSenha({ ...data, titulo: 'Conta criada' });
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível criar a conta');
    } finally { setBusy(false); }
  };

  const mudarPapel = async (m, role) => {
    try {
      await api.put(`/admin/staff/${m.admin_id}/role`, { role });
      success(`${m.full_name} passou a ${dados.papeis[role]?.rotulo}.`);
      carregar();
    } catch (err) { error(err.response?.data?.message || 'Não foi possível mudar o papel'); }
  };

  const alternarZona = async (m, zoneId) => {
    const actuais = (m.zonas || []).map((z) => z.zone_id);
    const novas = actuais.includes(zoneId)
      ? actuais.filter((z) => z !== zoneId)
      : [...actuais, zoneId];
    try {
      await api.put(`/admin/staff/${m.admin_id}/zones`, { zonas: novas });
      carregar();
    } catch (err) { error(err.response?.data?.message || 'Não foi possível atribuir a zona'); }
  };

  // Perguntas de confirmação nesta página e não em window.confirm: a caixa do
  // navegador é bloqueada em parte dos contextos onde a equipa trabalha, e o
  // botão ficava a não fazer nada — sem erro, sem explicação.
  const [aConfirmar, setAConfirmar] = useState(null); // { titulo, texto, rotulo, accao }

  const alternarEstado = async (m) => {
    const novo_ = m.status === 'active' ? 'suspended' : 'active';
    if (novo_ === 'suspended') {
      return setAConfirmar({
        titulo: `Suspender ${m.full_name}`,
        texto: 'A sessão dele termina já. A conta mantém-se e pode voltar a ser activada.',
        rotulo: 'Suspender',
        accao: () => mudarEstado(m, novo_),
      });
    }
    return mudarEstado(m, novo_);
  };

  const mudarEstado = async (m, novo_) => {
    setAConfirmar(null);
    try {
      await api.put(`/admin/staff/${m.admin_id}/status`, { status: novo_ });
      carregar();
    } catch (err) { error(err.response?.data?.message || 'Não foi possível mudar o estado'); }
  };

  // Retirar é definitivo, por isso pede-se o motivo numa caixa desta página —
  // é esse motivo que fica no registo, ao lado de quem retirou e quando.
  const retirar = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/staff/${aRetirar.m.admin_id}/retirar`, { motivo: aRetirar.motivo.trim() });
      success(`${aRetirar.m.full_name} foi retirada da equipa. Fica no registo.`);
      setARetirar(null);
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível retirar a conta');
    } finally { setBusy(false); }
  };

  const reporSenha = (m) => setAConfirmar({
    titulo: `Nova palavra-passe para ${m.full_name}`,
    texto: 'A palavra-passe actual deixa de funcionar. A nova aparece aqui uma vez — copie-a antes de fechar.',
    rotulo: 'Gerar palavra-passe',
    accao: () => gerarSenha(m),
  });

  const gerarSenha = async (m) => {
    setAConfirmar(null);
    try {
      const { data } = await api.post(`/admin/staff/${m.admin_id}/reset-password`);
      setSenha({ ...data, email: m.email, titulo: 'Nova palavra-passe' });
    } catch (err) { error(err.response?.data?.message || 'Não foi possível repor'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-400" /> Colaboradores
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Contas de gestão e de terreno. Pode mudar o papel de alguém a qualquer momento.
          </p>
        </div>
        <button
          onClick={() => setNovo({ full_name: '', email: '', role: 'field_agent', zonas: [] })}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold"
        >
          <UserPlus className="w-4 h-4" /> Nova conta
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : (
        <ul className="space-y-2">
          {(dados.equipa || []).map((m) => (
            <li key={m.admin_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900/50">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-slate-100">{m.full_name}</div>
                  <div className="text-xs text-slate-500 break-all">{m.email}</div>
                </div>
                <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${CORES[m.role] || 'bg-slate-700 text-slate-300'}`}>
                  {m.papel_rotulo}
                </span>
                {m.status !== 'active' && (
                  <span className="px-2 py-1 rounded-md text-[11px] font-semibold bg-red-500/15 text-red-400">
                    Suspensa
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <label className="sr-only" htmlFor={`papel-${m.admin_id}`}>Papel de {m.full_name}</label>
                <select
                  id={`papel-${m.admin_id}`}
                  value={m.role}
                  disabled={!podeGerir(m)}
                  onChange={(e) => mudarPapel(m, e.target.value)}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs"
                >
                  {!papeisAtribuiveis.some(([k]) => k === m.role) && <option value={m.role}>{m.papel_rotulo}</option>}
                  {papeisAtribuiveis.map(([k, v]) => <option key={k} value={k}>{v.rotulo}</option>)}
                </select>

                <button onClick={() => reporSenha(m)} disabled={!podeGerir(m)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs hover:bg-slate-700">
                  <KeyRound className="w-3.5 h-3.5" /> Nova palavra-passe
                </button>

                <button onClick={() => alternarEstado(m)} disabled={!podeGerir(m)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-700
                    ${m.status === 'active' ? 'bg-slate-800 text-red-400' : 'bg-slate-800 text-green-400'}`}>
                  {m.status === 'active'
                    ? <><Ban className="w-3.5 h-3.5" /> Suspender</>
                    : <><CheckCircle2 className="w-3.5 h-3.5" /> Reactivar</>}
                </button>

                <button onClick={() => setARetirar({ m, motivo: '' })} disabled={!podeGerir(m)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 text-xs hover:bg-red-500/15 hover:text-red-400">
                  <X className="w-3.5 h-3.5" /> Retirar
                </button>
              </div>

              {/* Zonas — só para quem anda no terreno */}
              {precisaZonas(m.role) && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <MapPin className="w-3.5 h-3.5 text-slate-500" />
                  {zonasAtribuiveis.length === 0 && (
                    <span className="text-xs text-amber-500">Nenhuma zona ligada — ligue uma em Zonas.</span>
                  )}
                  {/* Só as zonas ligadas, mais as que este agente já tem mesmo
                      que entretanto tenham sido desligadas — senão a atribuição
                      ficava invisível e ninguém a conseguia retirar. */}
                  {zonasAtribuiveis
                    .filter((z) => z.activa || (m.zonas || []).some((x) => x.zone_id === z.zone_id))
                    .map((z) => {
                      const tem = (m.zonas || []).some((x) => x.zone_id === z.zone_id);
                      return (
                        <button key={z.zone_id} onClick={() => alternarZona(m, z.zone_id)} disabled={!podeGerir(m)}
                          aria-pressed={tem}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border
                            ${tem ? 'bg-blue-500/15 text-blue-300 border-blue-500/40' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                          {z.nome}{!z.activa && <span className="ml-1 text-amber-400">· desligada</span>}
                        </button>
                      );
                    })}
                  {(m.zonas || []).length === 0 && zonas.length > 0 && (
                    <span className="text-xs text-amber-500">Sem zona — não consegue cadastrar ninguém.</span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ── Nova conta ── */}
      {novo && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !busy && setNovo(null)}>
          <form onSubmit={criar} onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-bold text-white">Novo colaborador</h2>

            <div>
              <label htmlFor="nome" className="block text-sm text-slate-400 mb-1">Nome completo</label>
              <input id="nome" required value={novo.full_name}
                onChange={(e) => setNovo((n) => ({ ...n, full_name: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
            </div>

            <div>
              <label htmlFor="mail" className="block text-sm text-slate-400 mb-1">Email</label>
              <input id="mail" type="email" required value={novo.email}
                onChange={(e) => setNovo((n) => ({ ...n, email: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
            </div>

            <div>
              <label htmlFor="papel" className="block text-sm text-slate-400 mb-1">Papel</label>
              <select id="papel" value={novo.role}
                onChange={(e) => setNovo((n) => ({ ...n, role: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
                {papeisAtribuiveis.map(([k, v]) => <option key={k} value={k}>{v.rotulo}</option>)}
              </select>
            </div>

            {precisaZonas(novo.role) && (
              <div>
                <span className="block text-sm text-slate-400 mb-1">Zonas</span>
                <div className="flex gap-2 flex-wrap">
                  {zonasActivas.length === 0 && (
                    <span className="text-xs text-amber-500">Nenhuma zona ligada. Ligue uma em Zonas antes de criar o agente.</span>
                  )}
                  {zonasActivas.map((z) => {
                    const tem = novo.zonas.includes(z.zone_id);
                    return (
                      <button key={z.zone_id} type="button" aria-pressed={tem}
                        onClick={() => setNovo((n) => ({
                          ...n,
                          zonas: tem ? n.zonas.filter((x) => x !== z.zone_id) : [...n.zonas, z.zone_id],
                        }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border
                          ${tem ? 'bg-blue-500/15 text-blue-300 border-blue-500/40' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                        {z.nome}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1.5">Só aparecem zonas ligadas. Sem zona, o agente não consegue cadastrar ninguém.</p>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setNovo(null)} disabled={busy}
                className="px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300">Cancelar</button>
              <button type="submit" disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar conta
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Credenciais, mostradas uma vez ── */}
      {senha && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-5">
            <div className="flex items-start gap-3 mb-3">
              <h2 className="text-lg font-bold text-white flex-1">{senha.titulo}</h2>
              <button onClick={() => setSenha(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-400 mb-3">
              Entregue estes dados à pessoa. <strong className="text-amber-300">Esta palavra-passe
              não volta a ser mostrada</strong> — se a perder, gere outra aqui.
            </p>

            <div className="rounded-xl bg-slate-800 p-3 space-y-2 font-mono text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-xs w-16">Email</span>
                <span className="flex-1 break-all text-slate-200">{senha.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-xs w-16">Senha</span>
                <span className="flex-1 text-blue-300">{senha.senha_temporaria}</span>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(`${senha.email}\n${senha.senha_temporaria}`);
                    success('Copiado.');
                  }}
                  aria-label="Copiar credenciais"
                  className="grid place-items-center w-9 h-9 rounded-lg bg-slate-700 text-slate-300">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-3">
              Peça à pessoa que a mude em “A minha conta” logo no primeiro acesso.
            </p>

            <button onClick={() => setSenha(null)}
              className="w-full mt-4 py-3 rounded-xl bg-blue-600 text-white font-semibold">
              Já guardei
            </button>
          </div>
        </div>
      )}

      {aConfirmar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog" aria-modal="true" aria-label={aConfirmar.titulo}>
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5">
            <h2 className="text-lg font-bold text-white">{aConfirmar.titulo}</h2>
            <p className="mt-1 text-sm text-slate-400">{aConfirmar.texto}</p>
            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" onClick={() => setAConfirmar(null)}
                className="px-4 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">
                Cancelar
              </button>
              <button type="button" onClick={aConfirmar.accao}
                className="px-4 min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold">
                {aConfirmar.rotulo}
              </button>
            </div>
          </div>
        </div>
      )}

      {aRetirar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog" aria-modal="true" aria-label={`Retirar ${aRetirar.m.full_name}`}>
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5">
            <h2 className="text-lg font-bold text-white">Retirar {aRetirar.m.full_name}</h2>
            <p className="mt-1 text-sm text-slate-400">
              A conta deixa de entrar e sai desta lista, mas não é apagada: fica no
              registo, com o motivo, quem a retirou e quando. O email fica livre
              para uma conta nova.
            </p>
            <textarea autoFocus rows={3}
              value={aRetirar.motivo}
              onChange={(e) => setARetirar((a) => ({ ...a, motivo: e.target.value }))}
              placeholder="Ex.: conta de teste que já não é precisa."
              className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100" />
            {aRetirar.motivo.trim().length < 5 && (
              <p className="mt-1 text-xs text-amber-400">Escreva o motivo — fica no registo.</p>
            )}
            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" onClick={() => setARetirar(null)} disabled={busy}
                className="px-4 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">
                Cancelar
              </button>
              <button type="button" onClick={retirar}
                disabled={busy || aRetirar.motivo.trim().length < 5}
                className="px-4 min-h-[44px] rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50">
                {busy ? 'A retirar…' : 'Retirar conta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPage;
